<!--
  자료실 — 도서 상세 / 다중 선택 다운로드 (guide01.md §6, 승인 강사 전용)
  · GET /instructor/materials/books/{barcode}
  · 다운로드 자료 표: 음원(그룹 행 + '개별 파일 보기' 펼침) 먼저 → PDF 순
  · 체크박스 다중 선택 → POST /instructor/materials/books/{barcode}/download (zip blob)
-->
<template>
  <div>
    <a-page-head
      title="도서 상세"
      :breadcrumbs="[
        { label: '자료실', to: { name: 'MaterialsBooks' } },
        { label: book ? book.title : '도서 상세' },
      ]"
    >
      <template #actions>
        <a-btn kind="ghost" @click="$router.push({ name: 'MaterialsBooks' })">목록으로</a-btn>
      </template>
    </a-page-head>

    <div v-if="loading" class="p-detail__loading">도서 정보를 불러오는 중입니다…</div>

    <template v-else-if="book">
      <!-- 상단 요약 -->
      <a-card bare class="p-detail__summary-card">
        <div class="p-detail__summary">
          <div class="p-detail__cover">
            <img
              v-if="book.cover_image_url && !coverError"
              :src="book.cover_image_url"
              :alt="book.title"
              @error="coverError = true"
            />
            <div v-else class="p-detail__cover-fallback">📘</div>
          </div>
          <div class="p-detail__head">
            <div class="p-detail__badge-row">
              <a-pill kind="yellow" :dot="false">선생님 전용 자료입니다</a-pill>
            </div>
            <h2 class="p-detail__title">{{ book.title }}</h2>
            <dl class="p-detail__meta">
              <div v-if="book.level"><dt>레벨</dt><dd>{{ book.level }}</dd></div>
              <div v-if="book.publisher"><dt>출판사</dt><dd>{{ book.publisher }}</dd></div>
              <div><dt>등록일</dt><dd>{{ $formatDate(book.created_at) }}</dd></div>
              <div><dt>바코드</dt><dd class="p-mono">{{ book.barcode }}</dd></div>
            </dl>
          </div>
        </div>
      </a-card>

      <!-- 다운로드 자료 -->
      <a-card bare class="p-detail__materials-card">
        <div class="p-detail__materials-head">
          <div class="p-detail__materials-title">다운로드 자료</div>
          <div class="p-detail__materials-actions">
            <span v-if="selected.length" class="p-detail__selected-count">{{ selected.length }}개 선택됨</span>
            <a-btn kind="primary" :disabled="!selected.length || downloading" @click="downloadSelected">
              {{ downloading ? '다운로드 중…' : '선택 자료 다운로드' }}
            </a-btn>
          </div>
        </div>

        <a-empty v-if="!hasAudio && !hasPdf" title="등록된 자료가 없습니다" sub="자료가 등록되면 이곳에 표시됩니다." />

        <table v-else class="p-detail__table">
          <thead>
            <tr>
              <th class="p-detail__th-check"></th>
              <th>자료명</th>
              <th class="p-detail__th-size">용량</th>
              <th class="p-detail__th-date">등록일</th>
            </tr>
          </thead>
          <tbody>
            <!-- 음원 그룹 (먼저 표시) -->
            <template v-if="hasAudio">
              <tr class="p-detail__group-row">
                <td class="p-detail__td-check">
                  <input
                    type="checkbox"
                    :checked="allAudioSelected"
                    :indeterminate="someAudioSelected"
                    @change="toggleAllAudio($event.target.checked)"
                  />
                </td>
                <td>
                  <div class="p-detail__group-name">
                    <span class="p-detail__type-icon">🎧</span>
                    전체 음원 <span class="p-mono">({{ audioFiles.length }}트랙)</span>
                  </div>
                </td>
                <td class="p-detail__td-size p-mono">{{ formatBytes(audioTotalSize) }}</td>
                <td class="p-detail__td-date">
                  <button class="p-detail__expand-btn" @click="audioExpanded = !audioExpanded">
                    {{ audioExpanded ? '개별 파일 접기 ▲' : '개별 파일 보기 ▼' }}
                  </button>
                </td>
              </tr>
              <template v-if="audioExpanded">
                <tr v-for="f in audioFiles" :key="'audio-' + f.filename" class="p-detail__track-row">
                  <td class="p-detail__td-check">
                    <input
                      type="checkbox"
                      :checked="isSelected('audio', f.filename)"
                      @change="toggleSelect('audio', f.filename, $event.target.checked)"
                    />
                  </td>
                  <td class="p-detail__td-track-name">{{ f.filename }}</td>
                  <td class="p-detail__td-size p-mono">{{ formatBytes(f.file_size) }}</td>
                  <td class="p-detail__td-date p-mono">{{ $formatDate(f.created_at) }}</td>
                </tr>
              </template>
            </template>

            <!-- PDF (음원 다음) -->
            <tr v-for="f in pdfFiles" :key="'pdf-' + f.filename">
              <td class="p-detail__td-check">
                <input
                  type="checkbox"
                  :checked="isSelected('pdf', f.filename)"
                  @change="toggleSelect('pdf', f.filename, $event.target.checked)"
                />
              </td>
              <td>
                <div class="p-detail__group-name">
                  <span class="p-detail__type-icon">📄</span>
                  {{ f.filename }}
                </div>
              </td>
              <td class="p-detail__td-size p-mono">{{ formatBytes(f.file_size) }}</td>
              <td class="p-detail__td-date p-mono">{{ $formatDate(f.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </a-card>
    </template>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';

export default {
  name: 'MaterialsBooksDetail',
  components: { APageHead, ACard, AEmpty, ABtn, APill },
  data() {
    return {
      barcode: '',
      book: null,
      loading: true,
      coverError: false,
      audioExpanded: false,
      selected: [], // [{ assetType, filename }]
      downloading: false,
    };
  },
  computed: {
    audioFiles() {
      return this.book?.materials?.audio || [];
    },
    pdfFiles() {
      return this.book?.materials?.pdf || [];
    },
    hasAudio() {
      return this.audioFiles.length > 0;
    },
    hasPdf() {
      return this.pdfFiles.length > 0;
    },
    audioTotalSize() {
      return this.audioFiles.reduce((sum, f) => sum + (f.file_size || 0), 0);
    },
    selectedAudioCount() {
      return this.selected.filter((s) => s.assetType === 'audio').length;
    },
    allAudioSelected() {
      return this.hasAudio && this.selectedAudioCount === this.audioFiles.length;
    },
    someAudioSelected() {
      return this.selectedAudioCount > 0 && !this.allAudioSelected;
    },
  },
  created() {
    this.barcode = this.$route.query.barcode;
    if (!this.barcode) {
      this.$router.replace({ name: 'MaterialsBooks' });
      return;
    }
    this.fetchBook();
  },
  methods: {
    async fetchBook() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/instructor/materials/books/${this.barcode}`,
        });
        this.book = res.data;
      } catch (e) {
        if (e.response?.status === 404) {
          this.$notifyVue({ type: 'error', title: '자료실', text: '도서를 찾을 수 없습니다.' });
        } else if (e.response?.status === 403) {
          this.$notifyVue({ type: 'warn', title: '자료실', text: '승인된 강사만 이용할 수 있습니다.' });
        } else {
          console.error('도서 상세 조회 실패:', e);
          this.$notifyVue({ type: 'error', title: '자료실', text: '도서 정보를 불러오지 못했습니다.' });
        }
        this.$router.replace({ name: 'MaterialsBooks' });
      } finally {
        this.loading = false;
      }
    },
    selectionKey(assetType, filename) {
      return `${assetType}/${filename}`;
    },
    isSelected(assetType, filename) {
      return this.selected.some((s) => s.assetType === assetType && s.filename === filename);
    },
    toggleSelect(assetType, filename, checked) {
      if (checked) {
        if (!this.isSelected(assetType, filename)) {
          this.selected.push({ assetType, filename });
        }
      } else {
        this.selected = this.selected.filter(
          (s) => !(s.assetType === assetType && s.filename === filename)
        );
      }
    },
    toggleAllAudio(checked) {
      // 음원 전체 선택/해제 — 기존 pdf 선택은 유지
      this.selected = this.selected.filter((s) => s.assetType !== 'audio');
      if (checked) {
        this.selected.push(...this.audioFiles.map((f) => ({ assetType: 'audio', filename: f.filename })));
      }
    },
    formatBytes(bytes) {
      if (!bytes && bytes !== 0) return '-';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    },
    async downloadSelected() {
      if (!this.selected.length || this.downloading) return;
      this.downloading = true;
      try {
        const res = await this.$axios({
          method: 'post',
          url: `/instructor/materials/books/${this.barcode}/download`,
          data: { selections: this.selected },
          responseType: 'blob',
          loadingBar: true,
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.barcode}_materials.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.$notifyVue({ type: 'success', title: '다운로드', text: '자료 다운로드가 시작되었습니다.' });
      } catch (e) {
        // responseType:'blob'이면 에러 응답 본문도 Blob → text 복원 후 메시지 추출
        let msg = '다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.';
        try {
          const parsed = JSON.parse(await e.response.data.text());
          msg = parsed.message || parsed.detail || msg;
        } catch (_) { /* 본문 파싱 불가 시 기본 메시지 유지 */ }
        this.$notifyVue({ type: 'error', title: '다운로드 실패', text: msg });
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink-soft; }

.p-detail__loading {
  padding: 48px;
  text-align: center;
  font-size: 13px;
  color: $p-ink-mid;
}

/* 상단 요약 */
.p-detail__summary-card { margin-bottom: 20px; }
.p-detail__summary {
  display: flex;
  gap: 24px;
  padding: 20px;

  @media (max-width: 700px) { flex-direction: column; }
}
.p-detail__cover {
  width: 160px;
  aspect-ratio: 3 / 4;
  flex-shrink: 0;
  border: 1px solid $p-line;
  border-radius: 10px;
  overflow: hidden;
  background: $p-line-soft;
  display: grid;
  place-items: center;

  img { width: 100%; height: 100%; object-fit: cover; }
}
.p-detail__cover-fallback { font-size: 44px; opacity: 0.5; }
.p-detail__head {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.p-detail__badge-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
.p-detail__title {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: $p-ink;
  margin: 0 0 16px;
  line-height: 1.3;
}
.p-detail__meta {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  > div { display: flex; font-size: 13px; }
  dt {
    width: 80px;
    flex-shrink: 0;
    color: $p-ink-soft;
    font-weight: 700;
    font-size: 12px;
  }
  dd { margin: 0; color: $p-ink; }
}

/* 다운로드 자료 */
.p-detail__materials-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid $p-line;
}
.p-detail__materials-title {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: $p-ink;
}
.p-detail__materials-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-detail__selected-count {
  font-size: 12px;
  font-weight: 700;
  color: $p-point-deep;
}

.p-detail__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-soft;
    letter-spacing: 0.04em;
    border-bottom: 1px solid $p-line;
    background: $p-bg;
  }
  td {
    padding: 12px 16px;
    border-bottom: 1px solid $p-line-soft;
    color: $p-ink;
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: 0; }

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    accent-color: $p-point-deep;
    cursor: pointer;
  }
}
.p-detail__th-check, .p-detail__td-check { width: 44px; text-align: center; }
.p-detail__th-size, .p-detail__td-size { width: 110px; white-space: nowrap; }
.p-detail__th-date, .p-detail__td-date { width: 150px; white-space: nowrap; }

.p-detail__group-row td { background: rgba(255, 201, 60, 0.06); }
.p-detail__group-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  word-break: break-all;
}
.p-detail__type-icon { font-size: 15px; }
.p-detail__expand-btn {
  border: 1px solid $p-line;
  background: $p-panel;
  border-radius: 6px;
  padding: 5px 10px;
  font-family: $p-font-body;
  font-size: 11px;
  font-weight: 700;
  color: $p-ink-mid;
  cursor: pointer;

  &:hover { background: $p-bg; color: $p-ink; }
}
.p-detail__track-row td { background: $p-bg; }
.p-detail__td-track-name {
  padding-left: 40px !important;
  font-size: 12px;
  color: $p-ink2;
  word-break: break-all;
}
</style>
